<template>
  <div :class="`apply-form company_${currentCompanyId}`">
    <template v-if="!loading">
      <div class="text-center">
        <h1 v-if="joboffer">{{ jobName }}</h1>
      </div>

      <v-alert v-if="showErrorAlert" type="error" dismissible class="mt-4">
        {{ errorAlertMessage }}
      </v-alert>
      <div
        v-if="applicationSubmitted"
        class="mt-8 text-center application-submitted__info"
        v-html="$t('application.submitConfirmationMessage')"
      />
      <div
        v-else-if="applicationSaved"
        class="mt-8 text-center application-submitted__info"
        v-html="$t('application.saveConfirmationMessage')"
      />
      <template v-else>
        <v-alert
          v-if="errors.length > 0"
          rounded
          text
          type="error"
          :icon="false"
          class="mt-4"
        >
          {{ $t('validation.summaryMessage') }}

          <ul>
            <li v-for="(label, index) in errors" :key="`error_index_${index}`">
              {{ label }}
            </li>
          </ul>
        </v-alert>

        <validation-observer ref="observer" v-slot="{ handleSubmit }">
          <form @submit.prevent="handleSubmit(onSubmit)">
            <div
              class="content-box content-box__color-secondary content-box__dense content-box__documents mt-8 pt-16"
              :class="{ 'has-photo-upload': hasPhotoUpload }"
            >
              <documents-form
                v-model="application"
                :documents-settings="documentsSettings"
                :joboffer-id="joboffer.jobofferId"
                @file-parsed="handleParsingData"
              ></documents-form>
            </div>

            <div
              class="content-box content-box__color-secondary content-box__dense mt-8 pt-16"
            >
              <personal-data-form
                v-model="application"
                :short-form="false"
                :joboffer="joboffer"
                :basics-settings="personalBasicsSettings"
                ref="personalDataForm"
              />
            </div>

            <div
              v-if="showPreviousJobs"
              class="content-box content-box__color-secondary content-box__dense mt-8 pt-16"
            >
              <previous-jobs-list v-model="previousJobs" />
            </div>

            <template v-if="showQuestions">
              <questions-form v-model="questions" />
            </template>
            <template v-if="allowCustomLanguages || allowCustomSkills">
              <additional-questions
                v-model="additionalQuestions"
                :allow-custom-languages="allowCustomLanguages"
                :allow-custom-skills="allowCustomSkills"
              />
            </template>

            <div
              v-if="
                !isInternal &&
                channelSettings.channelValidationMode !==
                  fieldValidationMode.NotPrompted
              "
              class="content-box content-box__color-secondary content-box__dense mt-8 pt-16"
            >
              <channel-form
                v-model="application"
                :channel-settings="channelSettings"
              />
            </div>

            <div
              v-if="acceptTalentpoolInApplicationProcess"
              class="content-box content-box__color-secondary content-box__dense mt-8 pt-16"
            >
              <h3>
                {{
                  $t('application.acceptTalentpoolInApplicationProcessHeadline')
                }}
              </h3>
              <v-checkbox
                v-model="talentpoolAccepted"
                :label="$t('application.acceptTalentpoolInApplicationProcess')"
                hide-details="auto"
              />
            </div>

            <div
              class="content-box content-box__color-secondary content-box__dense mt-8 pt-16"
            >
              <privacy-policy
                v-model="privacyPolicyConfirmed"
                :short-privacy-policy="shortPrivacyPolicy"
                ref="privacyPolicy"
              />
            </div>

            <div
              v-if="showRegisterPrompt"
              class="content-box content-box__color-secondary content-box__dense mt-8 pt-16"
            >
              <h3 class="mb-2">{{ $t('register.title') }}</h3>
              <p>
                {{
                  accountRequired
                    ? $t('register.mandatoryNotice')
                    : $t('register.description')
                }}
              </p>
              <v-btn color="primary" @click="openRegister">
                {{ $t('register.button') }}
              </v-btn>
            </div>

            <div
              v-if="resumeApplicationEnabled"
              class="content-box content-box__color-secondary content-box__dense mt-8 pt-16"
            >
              <application-resume-later
                @save="saveApplication"
                :submitting="submitting"
              />
            </div>

            <div
              class="content-box content-box__color-secondary content-box__dense mt-8 pt-16"
            >
              <application-summary
                @submit="submitApplication"
                :submitting="submitting"
                :disabled="accountRequired"
              />
            </div>
          </form>
        </validation-observer>
      </template>
    </template>

    <template v-else>
      <v-row>
        <v-col cols="12" sm="6" offset-sm="3">
          <div style="height: calc(100vh - 250px); padding-top: 250px">
            <v-progress-linear
              indeterminate
              rounded
              color="primary"
              height="10"
            />
          </div>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { v4 as uuid } from 'uuid';
import { eventBus } from '@/event-bus.js';

import applicationService from '@/services/ApplicationService.js';
import TrackingService from '@/services/TrackingService';
import { readUtm } from '@/helpers/UtmHelper.js';
import { readReferrerAttribution } from '@/helpers/ReferrerHelper.js';

import validationModeEnums from '@/enum/field-validation-mode.js';
import questionTypeEnums from '@/enum/question-type.js';
import basicsFieldsEnums from '@/enum/application-basics-fields.js';

import applicationSummary from '@/components/application/application-summary.vue';
import {
  saveApplicationDraft,
  loadApplicationDraft,
  clearApplicationDraft,
  mergeApplicationDraft
} from '@/helpers/ApplicationDraftStorage.js';
import applicationResumeLater from '@/components/application/application-resume-later.vue';
import channelForm from '@/components/application/channel-form.vue';
import documentsForm from '@/components/application/documents-form.vue';
import personalDataForm from '@/components/application/personal-data.vue';
import previousJobsList from '@/components/application/previous-jobs-list.vue';
import privacyPolicy from '@/components/application/privacy-policy.vue';
import questionsForm from '@/components/application/questions/questions-form.vue';
import additionalQuestions from '@/components/application/questions/additional-questions-form.vue';

export default {
  data: () => ({
    application: {
      title: '',
      firstName: '',
      lastName: '',
      email: '',
      confirmEmail: '',
      salutationId: null,
      channel: '',
      channelComment: '',
      isInternal: false,
      referrer: '',
      photo: null,
      cv: [],
      letter: [],
      letterText: '',
      documents: [],
      graphs: [],
      customAttachments: {}
    },
    joboffer: null,
    basics: [],
    documentsSettings: null,
    channelSettings: null,
    questions: null,
    additionalQuestions: [],
    allowCustomLanguages: false,
    allowCustomSkills: false,
    privacyPolicyConfirmed: false,
    talentpoolAccepted: false,
    isValid: false,
    loading: true,
    errors: [],
    submitting: false,
    applicationSaved: false,
    applicationSubmitted: false,
    showErrorAlert: false,
    errorAlertMessage: '',
    resumeApplicationEnabled: false,
    previousJobs: [],
    formStarted: false,
    applicationStartTrackingReady: false,
    applicationCloseTracked: false
  }),

  props: {
    jobofferId: {
      type: Number,
      required: true
    },
    applicationData: {
      type: Object,
      required: false,
      default: null
    }
  },

  computed: {
    ...mapState({
      currentLanguage: 'currentLanguage'
    }),

    ...mapGetters({
      heroClaim: 'heroClaim'
    }),

    ...mapGetters('companyModule', {
      isInternal: 'userIsInternal'
    }),

    ...mapGetters('candidateModule', {
      candidateIsAuthenticated: 'isAuthenticated',
      candidateProfile: 'profile'
    }),

    // Whether the signed-in candidate declared themselves internal in their
    // profile. Informational only — never used to gate internal-job access.
    profileIsInternal() {
      return !!(this.candidateProfile && this.candidateProfile.isInternal);
    },

    ...mapState('companyModule', {
      candidateAccountMode: 'candidateAccountMode'
    }),

    showRegisterPrompt() {
      return this.candidateAccountMode > 0 && !this.candidateIsAuthenticated;
    },

    accountRequired() {
      return this.candidateAccountMode === 2 && !this.candidateIsAuthenticated;
    },

    ...mapState('jobsModule', {
      joboffers: 'joboffers'
    }),

    ...mapState('companyModule', {
      slug: 'slug',
      currentCompanyId: 'currentCompanyId',
      disableQuestionsForInternalApplications:
        'disableQuestionsForInternalApplications',
      usePreviousJobsListForm: 'usePreviousJobsListForm',
      acceptTalentpoolInApplicationProcess:
        'acceptTalentpoolInApplicationProcess'
    }),

    ...mapState('applicationModule', {
      formId: 'formId',
      workData: 'workData',
      educationData: 'educationData'
    }),

    fieldValidationMode() {
      return validationModeEnums.FieldValidationModes;
    },

    basicsFields() {
      return basicsFieldsEnums.ApplicationBasicsFields;
    },

    hasPhotoUpload() {
      if (!this.documentsSettings) {
        return false;
      }

      return (
        this.documentsSettings.photoValidationMode !==
        this.fieldValidationMode.NotPrompted
      );
    },

    questionType() {
      return questionTypeEnums.QuestionType;
    },

    showPreviousJobs() {
      return this.isInternal && this.usePreviousJobsListForm;
    },

    showQuestions() {
      if (!this.questions) {
        return false;
      }

      if (this.isInternal && this.disableQuestionsForInternalApplications) {
        return false;
      }

      return true;
    },

    personalBasicsSettings() {
      return this.basics.filter(
        (x) => x.dataDefinitionId !== this.basicsFields.PreviousJobs
      );
    },

    applicationStartPayload() {
      return {
        application: this.application,
        basics: this.basics,
        previousJobs: this.previousJobs,
        questions: this.questions,
        additionalQuestions: this.additionalQuestions,
        talentpoolAccepted: this.talentpoolAccepted,
        privacyPolicyConfirmed: this.privacyPolicyConfirmed,
        workData: this.workData,
        educationData: this.educationData
      };
    },

    jobName() {
      return this.isInternal
        ? this.localize(this.joboffer.internalName) || this.localize(this.joboffer.name)
        : this.localize(this.joboffer.name);
    }
  },

  watch: {
    async slug() {
      await this.initializeData();
    },

    previousJobs: {
      handler() {
        this.updatePreviousJobsValue();
      },
      deep: true
    },

    applicationStartPayload: {
      handler() {
        this.markApplicationFormStarted();
      },
      deep: true
    }
  },

  async mounted() {
    await this.initializeData();

    // Pre-fill the form from the signed-in candidate's profile (only fields that
    // are still empty, so a resumed application or CV parse always takes priority).
    this.$nextTick(async () => {
      await this.prefillFromProfile();
    });

    // Restore any draft the candidate left behind when they went to register.
    this.restoreApplicationDraft();

    window.addEventListener('beforeunload', this.trackApplicationClose);

    if (!this.formId) {
      this.setFormId(uuid());
    }

    this.shortPrivacyPolicy = await this.loadShortPrivacyPolicy({
      slug: this.slug,
      jobofferId: this.joboffer.jobofferId,
      internal: this.isInternal
    });
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.trackApplicationClose);
    this.trackApplicationClose();
  },

  methods: {
    ...mapActions('jobsModule', {
      loadJobs: 'loadJobs'
    }),

    ...mapActions('applicationModule', {
      loadForm: 'loadForm',
      save: 'saveApplication',
      submit: 'submitApplication'
    }),

    ...mapActions('companyModule', {
      loadShortPrivacyPolicy: 'loadShortPrivacyPolicy'
    }),

    ...mapActions('candidateModule', {
      loadCandidateProfile: 'loadProfile',
      reuseCandidateDocument: 'reuseDocument'
    }),

    ...mapMutations('applicationModule', {
      setFormId: 'setFormId'
    }),

    ...mapMutations('companyModule', {
      setCompanyId: 'setCurrentCompanyId'
    }),

    async prefillFromProfile() {
      if (!this.candidateIsAuthenticated) {
        return;
      }

      await this.loadCandidateProfile();

      const profile = this.candidateProfile;
      if (!profile || !profile.exists) {
        return;
      }

      this.setApplicationValueIfEmpty('title', profile.title);
      this.setApplicationValueIfEmpty('firstName', profile.firstName);
      this.setApplicationValueIfEmpty('lastName', profile.lastName);
      this.setApplicationValueIfEmpty('email', profile.email);
      this.setApplicationValueIfEmpty('confirmEmail', profile.email);

      if (
        (this.application.salutationId === null ||
          this.application.salutationId === '') &&
        profile.salutationId !== null &&
        profile.salutationId !== undefined
      ) {
        this.$set(
          this.application,
          'salutationId',
          profile.salutationId.toString()
        );
      }

      this.prefillBasicsFromProfile(profile);

      await this.prefillDocumentsFromProfile(profile);
    },

    setApplicationValueIfEmpty(field, value) {
      if (value && !this.application[field]) {
        this.$set(this.application, field, value);
      }
    },

    // Sets the salutation only when none has been chosen yet. Kept separate from
    // setApplicationValueIfEmpty because the value must be stringified and 0/null
    // salutations from the parser should be ignored.
    setSalutationIfEmpty(salutation) {
      if (salutation === null || salutation === undefined) {
        return;
      }

      const current = this.application.salutationId;
      if (current === null || current === undefined || current === '') {
        this.$set(this.application, 'salutationId', salutation.toString());
      }
    },

    // Maps the profile's address and birthdate onto the matching application
    // basics fields (by data definition id), but only for fields this joboffer's
    // form actually shows and only when the field is still empty.
    prefillBasicsFromProfile(profile) {
      const fields = this.basicsFields;

      this.setBasicsValueIfEmpty(fields.AddressStreet, profile.street);
      this.setBasicsValueIfEmpty(fields.AddressZip, profile.zipCode);
      this.setBasicsValueIfEmpty(fields.AddressCity, profile.city);
      this.setBasicsValueIfEmpty(
        fields.Birthdate,
        this.toGermanDate(profile.birthdate)
      );
    },

    setBasicsValueIfEmpty(dataDefinitionId, value) {
      if (!value) {
        return;
      }

      const item = this.basics.find(
        (x) => x.dataDefinitionId === dataDefinitionId
      );

      if (item && (!item.value || item.value === '')) {
        item.value = value;
      }
    },

    // The profile stores the birthdate as an ISO date; basics date fields expect
    // the dd.MM.yyyy format the date picker emits.
    toGermanDate(isoDate) {
      if (!isoDate) {
        return '';
      }

      const [year, month, day] = isoDate.substring(0, 10).split('-');
      if (!year || !month || !day) {
        return '';
      }

      return `${day}.${month}.${year}`;
    },

    // Attaches the stored documents of the latest application to this form,
    // materializing each into the temporary bucket first. Only fills fields the
    // candidate has not already provided.
    async prefillDocumentsFromProfile(profile) {
      const documents = profile.documents || [];
      if (!documents.length) {
        return;
      }

      const attachments = [];
      for (const document of documents) {
        let fileReference = null;
        try {
          fileReference = await this.reuseCandidateDocument(document.id);
        } catch (e) {
          continue;
        }

        if (!fileReference) {
          continue;
        }

        // 0 = CV, 1 = Letter, 2 = Photo, 3 = Attachment
        if (document.type === 0 && !this.hasFiles(this.application.cv)) {
          this.$set(this.application, 'cv', [fileReference]);
        } else if (
          document.type === 1 &&
          !this.hasFiles(this.application.letter)
        ) {
          this.$set(this.application, 'letter', [fileReference]);
        } else if (document.type === 3) {
          attachments.push(fileReference);
        }
      }

      if (attachments.length && !this.hasFiles(this.application.documents)) {
        this.$set(this.application, 'documents', attachments);
      }
    },

    hasFiles(value) {
      return Array.isArray(value) && value.length > 0;
    },

    // Snapshots the in-progress application (keyed by joboffer) so it can be
    // restored when the candidate returns after registering, then opens the
    // register dialog.
    openRegister() {
      saveApplicationDraft(this.joboffer.jobofferId, this.application);
      eventBus.$emit('openRegister');
    },

    restoreApplicationDraft() {
      const draft = loadApplicationDraft(this.joboffer.jobofferId);
      // Only non-empty draft values are copied, so an empty draft field never
      // overwrites data already prefilled from the candidate's profile.
      this.application = mergeApplicationDraft(this.application, draft);
    },

    handleParsingData(data) {
      if (data) {
        // Only fill fields the candidate has not already provided (e.g. prefilled
        // from their profile or typed in) so the CV parser never overwrites them.
        this.setApplicationValueIfEmpty('firstName', data.cvData.firstName);
        this.setApplicationValueIfEmpty('lastName', data.cvData.lastName);
        this.setApplicationValueIfEmpty('email', data.cvData.emailAddress);
        this.setSalutationIfEmpty(data.cvData.salutation);

        for (const item of data.applicationBasics) {
          if (!item.value || item.value === '') {
            continue;
          }

          const basicItem = this.basics.find(
            (x) => x.dataDefinitionId === item.dataDefinitionId
          );

          if (!basicItem) {
            this.basics.push(item);
          } else if (!basicItem.value || basicItem.value === '') {
            basicItem.value = item.value.toString();
          }
        }

        for (const item of data.applicationQuestions) {
          if (!item.value || item.value === '') {
            continue;
          }

          let questionItem = null;
          let customType = 0;
          if (this.questions && item.languageId && item.languageId > 0) {
            customType = this.questionType.Language;
            questionItem = this.questions.find(
              (x) => x.languageId === parseInt(item.languageId)
            );

            item.value = parseInt(item.value);
          } else if (this.questions && item.code && item.code !== '') {
            customType = this.questionType.Competency;
            questionItem = this.questions.find((x) => x.code === item.code);

            item.value = applicationService.MapSkillsValue(item.value);
          }

          if (questionItem && !questionItem.value) {
            questionItem.value = parseInt(item.value);
          } else if (customType > 0) {
            this.additionalQuestions.push({
              questionId: -1,
              code: (item.languageId ?? item.code).toString(),
              value: item.value,
              questionType: item.questionType,
              questionText: item.questionText
            });
          }
        }

        if (data.userPhoto) {
          this.$set(this.application, 'photo', data.userPhoto);
        }

        if (data.graphs && data.graphs.length > 0) {
          this.$set(this.application, 'graphs', data.graphs);
        }
      }
    },

    async initializeData() {
      this.formStarted = false;
      this.applicationCloseTracked = false;
      this.applicationStartTrackingReady = false;
      this.loading = true;

      if (!this.slug) {
        return;
      }

      if (!this.joboffers || this.joboffers.length === 0) {
        await this.loadJobs(this.slug);
      }

      // Await the whole form load (which populates this.basics) so callers can
      // rely on it being finished — otherwise prefillFromProfile races ahead of
      // the basics being set.
      await new Promise((resolve) => {
        this.$nextTick(async () => {
          try {
            await this.loadFormData();
          } finally {
            resolve();
          }
        });
      });
    },

    async loadFormData() {
      this.joboffer = this.joboffers.find(
        (x) => x.jobofferId.toString() === this.jobofferId.toString()
      );

        this.setCompanyId(this.joboffer.companyId);
        const formData = await this.loadForm(this.jobofferId);
        document.title = `${this.$t('application.applyNow')}: ${this.jobName} - ${this.localize(this.joboffer.companyName)}`;

      this.resumeApplicationEnabled = formData.resumeApplicationEnabled;
      this.allowCustomLanguages = formData.allowCustomLanguages;
      this.allowCustomSkills = formData.allowCustomSkills;

      this.basics = formData.applicationBasicsSettings;
      if (!this.isInternal && this.basics.length > 0) {
        this.basics = this.basics.filter((x) => !x.internal);
      }

      if (this.isInternal && this.basics.length > 0) {
        this.basics = this.basics.filter((x) => !x.externalOnly);
      }

      this.ensureReactiveFormValues(this.basics);

      this.documentsSettings = {
        cvValidationMode: formData.cvValidationMode,
        documentsValidationMode: formData.documentsValidationMode,
        letterValidationMode: formData.letterValidationMode,
        photoValidationMode: formData.photoValidationMode,
        customAttachmentTypes: formData.customAttachmentTypes,
        uploadInfoText: formData.uploadInfoText
      };

      for (const customType of this.documentsSettings.customAttachmentTypes) {
        this.$set(this.application.customAttachments, customType.type, []);
      }

      this.channelSettings = {
        channels: formData.channels,
        channelValidationMode: formData.channelValidationMode,
        channelCommentValidationMode: formData.channelCommentValidationMode
      };

      if (
        formData.applicationQuestions &&
        formData.applicationQuestions.length > 0
      ) {
        this.questions = formData.applicationQuestions;
        this.ensureReactiveFormValues(this.questions);
      }

      if (this.applicationData) {
        this.$set(
          this.application,
          'sourceApplicationId',
          this.applicationData.sourceApplicationId
        );

        this.$set(this.application, 'referrer', this.applicationData.referrer);
        this.$set(this.application, 'title', this.applicationData.title);
        this.$set(
          this.application,
          'firstName',
          this.applicationData.firstName
        );

        this.$set(this.application, 'lastName', this.applicationData.lastName);

        this.$set(this.application, 'email', this.applicationData.email);
        this.$set(
          this.application,
          'salutationId',
          this.applicationData.salutationId?.toString()
        );

        this.$set(this.application, 'channel', this.applicationData.channel);
        this.$set(
          this.application,
          'channelComment',
          this.applicationData.channelComment
        );

        this.$set(
          this.application,
          'isInternal',
          this.applicationData.isInternal ?? 0
        );

        this.talentpoolAccepted =
          this.applicationData.acceptTalentpoolInApplicationProcess ?? false;

        this.$set(this.application, 'photo', this.applicationData.userPhoto);

        this.$set(this.application, 'cv', this.applicationData.cv);
        this.$set(
          this.application,
          'letterText',
          this.applicationData.letterText
        );

        this.$set(this.application, 'letter', this.applicationData.letter);
        this.$set(
          this.application,
          'documents',
          this.applicationData.documents
        );

        for (const customAttachments of this.applicationData
          .customAttachments) {
          this.$set(
            this.application.customAttachments,
            customAttachments.type,
            customAttachments.files
          );
        }

        this.$set(this.application, 'graphs', this.applicationData.graphs);
        for (const data of this.applicationData.basics) {
          const basic = this.basics.find(
            (x) => x.dataDefinitionId === data.dataDefinitionId
          );

          if (basic) {
            if (basic.fieldType === 6) {
              const value = parseInt(data.value);
              basic.value = isNaN(value) ? -1 : value;
            } else {
              basic.value = data.value;
            }
          }
        }

        for (const data of this.applicationData.questions) {
          const question = this.questions.find(
            (x) => x.questionId === data.questionId
          );

          if (question) {
            const value = parseInt(data.value);
            question.value = isNaN(value) ? -1 : value;
          }
        }

        for (const data of this.applicationData.additionalQuestions) {
          this.additionalQuestions.push({
            questionId: -1,
            code: data.code,
            value: data.value,
            questionType: data.questionType,
            questionText: data.questionText
          });
        }
      } else {
        // A candidate who declared themselves internal in their profile submits
        // as internal even on the public portal (informational only; it does not
        // grant internal-job access). On resume the stored value above is kept.
        this.$set(
          this.application,
          'isInternal',
          this.isInternal || this.profileIsInternal
        );
      }

      this.initializePreviousJobs();
      this.loading = false;
      this.$nextTick(() => {
        this.applicationStartTrackingReady = true;
      });

      TrackingService.flushPageView();
    },

    async saveApplication() {
      this.showErrorAlert = false;
      this.errorAlertMessage = '';
      this.resetValidation();

      const emailValid = await this.$refs.personalDataForm?.validateEmails?.();
      const privacyValid = await this.$refs.privacyPolicy?.validatePolicy?.();

      if (emailValid === false || privacyValid === false) {
        eventBus.$emit('errorMessage', this.$t('validation.formInvalid_save'));
        return;
      }

      this.submitting = true;

      try {
        this.application.slug = this.slug;
        this.application.acceptTalentpoolInApplicationProcess =
          this.talentpoolAccepted;
        this.application.jobofferId = this.joboffer.jobofferId;
        this.application.disableCvParsing = true;
        this.application.selectedLanguage = this.currentLanguage;
        this.application.referrer = this.getApplicationReferrer();
        this.application.basics = this.basics;

        if (this.usePreviousJobsForm && this.previousJobs.length > 0) {
          this.application.basics.push({
            dataDefinitionId: this.basicsFields.PreviousJobs,
            value: JSON.stringify(this.previousJobs)
          });
        }

        this.application.questions = this.questions;
        this.application.additionalQuestions = this.additionalQuestions;

        const response = await this.save(this.application);
        const result = await response.json();
        if (response.ok && result.success) {
          this.applicationSaved = true;
        } else {
          eventBus.$emit(
            'errorMessage',
            this.$t(`application.errors.${result.errorCode}`)
          );

          this.showErrorAlert = true;
          this.errorAlertMessage = this.$t(
            `application.errors.${result.errorCode}`
          );
        }

        window.scrollTo(0, 0);
      } catch (error) {
        console.log(error);
        eventBus.$emit(
          'errorMessage',
          'Es ist ein Fehler beim Speichern der Bewerbung aufgetreten'
        );
      }

      this.submitting = false;
    },

    async submitApplication() {
      this.showErrorAlert = false;
      this.errorAlertMessage = '';
      this.resetValidation();

      const result = await this.$refs.observer.validate();

      if (!result) {
        eventBus.$emit('errorMessage', this.$t('validation.formInvalid'));

        return;
      }

      this.submitting = true;

      try {
        this.application.slug = 'hidden-professionals';
        this.application.acceptTalentpoolInApplicationProcess =
          this.talentpoolAccepted;
        this.application.acceptPrivacyPolicy = this.privacyPolicyConfirmed;
        this.application.jobofferId = this.joboffer.jobofferId;
        this.application.disableCvParsing = true;
        this.application.selectedLanguage = this.currentLanguage;

        this.application.basics = this.basics;
        if (this.usePreviousJobsForm && this.previousJobs.length > 0) {
          this.application.basics.push({
            dataDefinitionId: this.basicsFields.PreviousJobs,
            value: JSON.stringify(this.previousJobs)
          });
        }

        this.application.questions = this.questions;
        this.application.additionalQuestions = this.additionalQuestions;
        this.application.referrer = this.getApplicationReferrer();
        this.application.utm = readUtm();
        this.application.workData = this.workData;
        this.application.educationData = this.educationData;

        const response = await this.submit(this.application);
        const result = await response.json();
        if (response.ok && result.success) {
          this.applicationSubmitted = true;
          clearApplicationDraft(this.joboffer.jobofferId);
          TrackingService.trackApplySubmit(this.joboffer.jobofferId);
        } else {
          eventBus.$emit(
            'errorMessage',
            this.$t(`application.errors.${result.errorCode}`)
          );

          this.showErrorAlert = true;
          this.errorAlertMessage = this.$t(
            `application.errors.${result.errorCode}`
          );
        }

        window.scrollTo(0, 0);
      } catch (error) {
        eventBus.$emit(
          'errorMessage',
          'Es ist ein Fehler beim Speichern der Bewerbung aufgetreten'
        );
      }

      this.submitting = false;
    },

    initializePreviousJobs() {
      if (!this.previousJobsItem) {
        return;
      }

      if (!this.previousJobsItem.value) {
        this.previousJobs = [];
        this.previousJobsItem.value = JSON.stringify(this.previousJobs);
        return;
      }

      try {
        const parsed = JSON.parse(this.previousJobsItem.value);
        this.previousJobs = Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        this.previousJobs = [];
      }
    },

    updatePreviousJobsValue() {
      if (!this.previousJobsItem) {
        return;
      }

      this.previousJobsItem.value = JSON.stringify(this.previousJobs ?? []);
    },

    getApplicationReferrer() {
      return this.application.referrer || readReferrerAttribution();
    },

    ensureReactiveFormValues(items) {
      if (!items) {
        return;
      }

      for (const item of items) {
        if (!Object.prototype.hasOwnProperty.call(item, 'value')) {
          this.$set(item, 'value', null);
        }
      }
    },

    resetValidation() {
      this.$refs.observer?.reset?.();
      this.$refs.personalDataForm?.resetEmailValidation?.();
      this.$refs.privacyPolicy?.resetPolicyValidation?.();
    },

    trackApplicationClose() {
      if (
        this.applicationCloseTracked ||
        !this.formStarted ||
        this.applicationSubmitted ||
        this.applicationSaved
      ) {
        return;
      }

      this.applicationCloseTracked = true;
      TrackingService.trackApplyCancel(this.jobofferId);
    },

    markApplicationFormStarted() {
      if (
        !this.applicationStartTrackingReady ||
        this.loading ||
        this.submitting ||
        this.formStarted
      ) {
        return;
      }

      this.trackApplyStart();
    },

    async trackApplyStart() {
      if (!this.formStarted) {
        this.formStarted = true;

        await TrackingService.trackApplyStart(this.jobofferId);
      }
    }
  },

  components: {
    applicationSummary,
    applicationResumeLater,
    channelForm,
    documentsForm,
    personalDataForm,
    previousJobsList,
    privacyPolicy,
    questionsForm,
    ValidationObserver,
    additionalQuestions
  }
};
</script>
<style scoped>
div >>> .v-input--has-state.error--text .v-messages__message::before {
  content: '⚠ ';
}
</style>
